import { useIntl } from 'react-intl'

import BaseCompactCard from './BaseCompactCard'

import LineArrowRightIcon from 'LuxKit/Icons/line/LineArrowRightIcon'
import { useRegionTimeFormat } from 'hooks/useRegionTimeFormat'
import { ItemMetadata } from 'tripPlanner/models'
import type {
  BusItem,
  FerryItem,
  OtherTransportItem,
  TrainItem,
} from 'tripPlanner/types/tripItem'
import type { DetailItemCardVariant } from '../DetailItemCard/common/types'

interface Props {
  item: BusItem | TrainItem | FerryItem | OtherTransportItem
  presentationType: 'start' | 'end' | 'intermediary'
  variant: DetailItemCardVariant
}

function CompactTransportCard({ item, presentationType, variant }: Props) {
  const intl = useIntl()
  let description: string | undefined

  const { type, startPlace, endPlace, startTime, endTime } = item
  const { icon: Icon, singularName } = ItemMetadata[type]

  const timeFormat = useRegionTimeFormat()

  const title = endPlace ? (
    <>
      {startPlace.name} <LineArrowRightIcon size="S" /> {endPlace.name}
    </>
  ) : (
    intl.formatMessage(
      {
        id: 'QPUKwRo4nHZVdSanZr0e',
        defaultMessage: '{transportType} from {placeName}',
        description: 'Transport compact card fallback title showing type and departure location',
      },
      { transportType: intl.formatMessage(singularName), placeName: startPlace.name },
    )
  )

  const isMultiDay =
    item.endDate && item.endDate.diff(item.startDate, 'days') > 0

  if (presentationType === 'start') {
    if (startTime) {
      description = `Departs ${startTime.format(timeFormat)}`
    }
    if (!isMultiDay && startTime && endTime) {
      description = intl.formatMessage(
        {
          id: '43D2cNqD_MN1ZnV2gDrD',
          defaultMessage: 'Departs {departureTime} · Arrives {arrivalTime}',
          description: 'Transport compact card label showing departure and arrival times',
        },
        {
          departureTime: startTime.format(timeFormat),
          arrivalTime: endTime.format(timeFormat),
        },
      )
    }
    if (isMultiDay && !startTime) {
      description = intl.formatMessage({
        id: 'EtAhExZsriQXqkW8QGCO',
        defaultMessage: 'Departure',
        description: 'Description label for a multi-day transport on the departure day',
      })
    }
  } else if (presentationType === 'intermediary') {
    description = intl.formatMessage({
      id: 'tZEV8OuKNeGAwlMrhCDR',
      defaultMessage: 'In transit',
      description: 'Description label for transport on intermediary transit days',
    })
  } else if (presentationType === 'end') {
    if (endTime) {
      description = `Arrives ${endTime.format(timeFormat)}`
    } else {
      description = intl.formatMessage({
        id: '-NkyUs5SM9xnNBjt11R6',
        defaultMessage: 'Arrival',
        description: 'Description label for transport on the arrival day without a specific time',
      })
    }
  }

  return (
    <BaseCompactCard
      item={item}
      icon={<Icon size="S" />}
      title={title}
      description={description}
      isPrimary={presentationType === 'start'}
      variant={variant}
    />
  )
}

export default CompactTransportCard
